"use client";

import React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Info, Loader, ShieldCheck, Building2, Layers3 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-8 text-center shadow-sm">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10">
          <ShieldCheck className="size-6 text-primary" />
        </div>
        <h3 className="mb-2 text-xl font-semibold text-foreground">Inquiry received</h3>
        <p className="mb-6 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Submit another inquiry
        </Button>
      </div>;
  }
  const inputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
  const textareaClasses = "flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors resize-y";
  const selectClasses = "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors appearance-none";
  return <Form onSubmit={handleSubmit}>
      <div className="grid gap-6 sm:grid-cols-2">
        <FormField control={control} name="agency_name" rules={{
        required: "Agency name is required"
      }} render={({
        field
      }) => <FormItem className="sm:col-span-2">
              <FormLabel>Agency / Company Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input type="text" className={inputClasses} placeholder="Acme Digital Agency" {...field} />
              </FormControl>
              <FormDescription>Enter the agency or company evaluating white-label.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_name" rules={{
        required: "Contact name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Primary Contact Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input type="text" className={inputClasses} placeholder="Jane Smith" {...field} />
              </FormControl>
              <FormDescription>The person we should coordinate with.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Contact Email <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input type="email" className={inputClasses} placeholder="jane.smith@acme.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Contact Phone</FormLabel>
              <FormControl>
                <input type="tel" className={inputClasses} placeholder="+1 (555) 123-4567" {...field} />
              </FormControl>
              <FormDescription>Optional, but useful for quicker follow-up.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_size" render={({
        field
      }) => <FormItem>
              <FormLabel>Company Size</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={selectClasses} {...field}>
                    <option value="" disabled>Select size</option>
                    {["1-10 employees", "11-50 employees", "51-200 employees", "201-500 employees", "500+ employees"].map((size, i) => <option key={i} value={size} data-index={i}>{size}</option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="size-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="inquiry_details" rules={{
        required: "Inquiry details are required"
      }} render={({
        field
      }) => <FormItem className="sm:col-span-2">
              <FormLabel>Inquiry Details / Delivery Model <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <textarea className={textareaClasses} placeholder="Describe your white-label goals, expected client volume, and how you want to deliver EloRAG under your brand." {...field} />
              </FormControl>
              <FormDescription>The more specific you are, the more relevant the follow-up will be.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <Alert variant="destructive" className="mt-6">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <div className="mt-8 flex justify-end">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Submitting inquiry...
            </> : "Request White-Label Demo"}
        </Button>
      </div>
    </Form>;
}
export default function AgencyWhiteLabelInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="agency-white-label-inquiry" className="bg-background py-16 lg:py-20">
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        <div ref={ref} className={cn("transition-all duration-700 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mb-10 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <Layers3 className="size-4" />
              Agency inquiry
            </div>

            <div className="mb-4 flex justify-center">
              <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Building2 className="size-6" />
              </div>
            </div>

            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Agency / White-Label Inquiry
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Discuss how EloRAG fits your reseller model, branded delivery goals, and client environment structure.
            </p>
          </div>

          <Alert className="mb-10 border-primary/20 bg-primary/5 shadow-sm">
            <Info className="size-5 text-primary" />
            <AlertTitle className="text-base font-semibold text-foreground">Important requirement for white-label</AlertTitle>
            <AlertDescription className="mt-2 text-sm text-muted-foreground">
              White-label requires an active <strong>Business plan or higher</strong>, a separate <strong>White-Label subscription</strong>, and at least <strong>one Capacity Pack</strong>.
            </AlertDescription>
          </Alert>

          <div className="rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <FormProvider formId="white_label_inquiry_form_book_a_demo_contact_sales" sectionName="agency-white-label-inquiry" defaultValues={{
            agency_name: "",
            contact_name: "",
            email: "",
            phone: "",
            company_size: "",
            inquiry_details: ""
          }} validationRules={{
            agency_name: {
              required: true
            },
            contact_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            company_size: {},
            inquiry_details: {
              required: true
            }
          }} submitText="Request White-Label Demo" successMessage="Thank you for your inquiry. Our team will review your details and contact you shortly.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}